import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { PageHeader } from '../components/PageHeader';
import {
  useAddContract,
  useContracts,
  useCreatePerson,
  useDeactivatePerson,
  useExpiringContracts,
  usePeople,
  useUpdatePerson,
} from '../hooks/useOps';
import type { EmploymentType, Person } from '../api/types';
import { formatDate, formatIDR, humanizeStatus, statusColor } from '../lib/format';

const EMPLOYMENT_TYPES: Array<{ value: EmploymentType; label: string }> = [
  { value: 'pkwt', label: 'PKWT — fixed term' },
  { value: 'pkwtt', label: 'PKWTT — permanent' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'intern', label: 'Intern' },
];

const PTKP_OPTIONS = ['TK0', 'TK1', 'TK2', 'TK3', 'K0', 'K1', 'K2', 'K3', 'HB'];

const EMPTY = {
  name: '',
  email: '',
  phone: '',
  jobTitle: '',
  employmentType: 'pkwt' as EmploymentType,
  startDate: '',
  endDate: '',
  ptkpStatus: '',
  monthlyCost: '',
};

export function PeoplePage() {
  const { data: people = [], isLoading, error } = usePeople();
  const { data: expiring = [] } = useExpiringContracts();
  const create = useCreatePerson();
  const update = useUpdatePerson();
  const deactivate = useDeactivatePerson();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Person | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);
  const [contractsFor, setContractsFor] = useState<Person | null>(null);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setFormError(null);
    setOpen(true);
  };
  const openEdit = (p: Person) => {
    setEditing(p);
    setForm({
      name: p.name,
      email: p.email ?? '',
      phone: p.phone ?? '',
      jobTitle: p.jobTitle,
      employmentType: p.employmentType,
      startDate: p.startDate,
      endDate: p.endDate ?? '',
      ptkpStatus: p.ptkpStatus ?? '',
      monthlyCost: p.monthlyCost ?? '',
    });
    setFormError(null);
    setOpen(true);
  };

  const submit = async () => {
    setFormError(null);
    const body = {
      name: form.name.trim(),
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      jobTitle: form.jobTitle.trim(),
      employmentType: form.employmentType,
      startDate: form.startDate,
      endDate: form.endDate || undefined,
      ptkpStatus: form.ptkpStatus || undefined,
      monthlyCost: form.monthlyCost ? Number(form.monthlyCost) : undefined,
    };
    try {
      if (editing) await update.mutateAsync({ id: editing.id, body });
      else await create.mutateAsync(body);
      setOpen(false);
    } catch (e) {
      setFormError((e as Error).message);
    }
  };

  const onDeactivate = (p: Person) => {
    if (window.confirm(`Deactivate ${p.name}? Records are retained; revoke their login separately.`)) {
      deactivate.mutate(p.id);
    }
  };

  const columns: GridColDef<Person>[] = [
    { field: 'name', headerName: 'Name', minWidth: 160, flex: 1 },
    { field: 'jobTitle', headerName: 'Job title', minWidth: 150, flex: 1 },
    {
      field: 'employmentType',
      headerName: 'Type',
      width: 110,
      renderCell: (p) => <Chip size="small" variant="outlined" label={p.value} />,
    },
    {
      field: 'monthlyCost',
      headerName: 'Monthly cost',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (v) => (v ? formatIDR(v as string) : '—'),
    },
    { field: 'startDate', headerName: 'Started', width: 120, valueGetter: (v) => formatDate(v as string) },
    {
      field: 'endDate',
      headerName: 'Ends',
      width: 120,
      valueGetter: (v) => (v ? formatDate(v as string) : '—'),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (p) => (
        <Chip size="small" variant="outlined" color={statusColor(p.value)} label={humanizeStatus(p.value)} />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 230,
      sortable: false,
      renderCell: (p) => (
        <Stack direction="row" gap={0.5}>
          <Button size="small" onClick={() => openEdit(p.row)}>
            Edit
          </Button>
          <Button size="small" onClick={() => setContractsFor(p.row)}>
            Contracts
          </Button>
          {p.row.status === 'active' && (
            <Button size="small" color="error" onClick={() => onDeactivate(p.row)}>
              Deactivate
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="People"
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add person
          </Button>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        The HR directory tracks employment facts — contract terms, probation, and leave. It does{' '}
        <strong>not</strong> rule on PKWT/PKWTT conversion or pesangon; those defer to a lawyer.
      </Alert>

      {expiring.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{expiring.length}</strong> contract/probation date(s) ending within 30 days:{' '}
          {expiring
            .slice(0, 5)
            .map((e) => `${e.personName} (${e.kind === 'contract_end' ? 'contract' : 'probation'} in ${e.daysRemaining}d)`)
            .join(', ')}
          {expiring.length > 5 ? '…' : ''}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ height: 560 }}>
        <DataGrid
          rows={people}
          columns={columns}
          getRowId={(r) => r.id}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        />
      </Paper>

      <PersonDialog
        open={open}
        editing={editing}
        form={form}
        setForm={setForm}
        formError={formError}
        onClose={() => setOpen(false)}
        onSubmit={submit}
        pending={create.isPending || update.isPending}
      />

      {contractsFor && (
        <ContractsDialog person={contractsFor} onClose={() => setContractsFor(null)} />
      )}
    </Box>
  );
}

function PersonDialog({
  open,
  editing,
  form,
  setForm,
  formError,
  onClose,
  onSubmit,
  pending,
}: {
  open: boolean;
  editing: Person | null;
  form: typeof EMPTY;
  setForm: (f: typeof EMPTY) => void;
  formError: string | null;
  onClose: () => void;
  onSubmit: () => void;
  pending: boolean;
}) {
  const valid = form.name.trim().length >= 2 && form.jobTitle.trim().length >= 2 && form.startDate;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add person'}</DialogTitle>
      <DialogContent>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        <Stack gap={2} sx={{ mt: 1 }}>
          <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth autoFocus />
          <TextField label="Job title" value={form.jobTitle} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} fullWidth />
          <Stack direction="row" gap={2}>
            <TextField label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} fullWidth />
            <TextField label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} fullWidth />
          </Stack>
          <TextField
            select
            label="Employment type"
            value={form.employmentType}
            onChange={(e) => setForm({ ...form, employmentType: e.target.value as EmploymentType })}
            fullWidth
          >
            {EMPLOYMENT_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" gap={2}>
            <TextField
              label="Start date"
              type="date"
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="End date (optional)"
              type="date"
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
          <Stack direction="row" gap={2}>
            <TextField
              select
              label="PTKP status (PPh 21)"
              value={form.ptkpStatus}
              onChange={(e) => setForm({ ...form, ptkpStatus: e.target.value })}
              fullWidth
            >
              <MenuItem value="">
                <em>Not set</em>
              </MenuItem>
              {PTKP_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Monthly cost (incl. BPJS employer share)"
              type="number"
              value={form.monthlyCost}
              onChange={(e) => setForm({ ...form, monthlyCost: e.target.value })}
              helperText="Split across projects by logged days (P&L)"
              fullWidth
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={pending || !valid} onClick={onSubmit}>
          {editing ? 'Save' : 'Add person'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const EMPTY_CONTRACT = {
  type: 'pkwt' as EmploymentType,
  startDate: '',
  endDate: '',
  probationEndDate: '',
  notes: '',
};

function ContractsDialog({ person, onClose }: { person: Person; onClose: () => void }) {
  const { data: contracts = [], isLoading, error } = useContracts(person.id);
  const add = useAddContract();
  const [form, setForm] = useState(EMPTY_CONTRACT);
  const [formError, setFormError] = useState<string | null>(null);

  const submit = async () => {
    setFormError(null);
    try {
      await add.mutateAsync({
        personId: person.id,
        body: {
          type: form.type,
          startDate: form.startDate,
          endDate: form.endDate || undefined,
          probationEndDate: form.probationEndDate || undefined,
          notes: form.notes.trim() || undefined,
        },
      });
      setForm(EMPTY_CONTRACT);
    } catch (e) {
      setFormError((e as Error).message);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Contracts — {person.name}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(error as Error).message}
          </Alert>
        )}
        {isLoading ? (
          <Typography color="text.secondary">Loading…</Typography>
        ) : contracts.length === 0 ? (
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No contracts recorded yet.
          </Typography>
        ) : (
          <Stack gap={1} sx={{ mb: 2 }}>
            {contracts.map((c) => (
              <Paper key={c.id} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                  <Chip size="small" variant="outlined" label={c.type} />
                  <Typography variant="body2">
                    {formatDate(c.startDate)} → {c.endDate ? formatDate(c.endDate) : 'open-ended'}
                  </Typography>
                  {c.probationEndDate && (
                    <Chip size="small" color="warning" variant="outlined" label={`probation ends ${formatDate(c.probationEndDate)}`} />
                  )}
                </Stack>
                {c.notes && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {c.notes}
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        )}

        <Divider sx={{ my: 2 }}>Add contract</Divider>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        <Stack gap={2}>
          <TextField
            select
            label="Type"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value as EmploymentType })}
            fullWidth
          >
            {EMPLOYMENT_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" gap={2}>
            <TextField label="Start" type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField label="End (optional)" type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField label="Probation end (optional)" type="date" value={form.probationEndDate} onChange={(e) => setForm({ ...form, probationEndDate: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
          </Stack>
          <TextField label="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} multiline minRows={2} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" disabled={add.isPending || !form.startDate} onClick={submit}>
          Add contract
        </Button>
      </DialogActions>
    </Dialog>
  );
}
